import { type SyntheticEvent, useEffect, useState } from "react";
import { type DropdownProps } from "semantic-ui-react";
import SortOptions from "../../../../enums/SortOptions";
import SearchInput from "../../../../components/searchInput/SearchInput";
import NoResults from "../../../../components/noResults/NoResults";
import ItemsView from "../../../../views/ItemsView/ItemsView";
import { gridSortingOptions } from "./sortOptions";
import { isEmpty, noop } from "lodash";
import NoSearchResults from "../../../../components/noSearchResults";
import { type AppDispatch, type RootState } from "../../../Application/globaltypes/redux";
import { connect, type ConnectedProps } from "react-redux";
import { ItemsTypes, SortingDirection, ViewType } from "enums";
import { type AccountPackOverview, type AssignAccountPack } from "features/Accounts/PackAssignmentModal/types";
import AddToAccountPackCard, {
  type Item,
} from "features/Accounts/PackAssignmentModal/packAssignmentSteps/packGridStep/addToAccountPackCard/AddToAccountPackCard";
import { PacksFilterForm } from "../../../../components/filterForms";
import {
  appliedFiltersSelector as availablePacksAppliedFiltersSelector,
  availablePacksFiltersSlice,
  filterOptionsSelector as availablePacksFilterOptionsSelector,
  isLoadingSelector as availablePacksFilterIsLoadingSelector,
  resetAppliedFilter as availablePacksResetAppliedFilter,
  setAppliedFilter as availablePacksSetAppliedFilter,
  setSearch as availablePacksSetSearch,
  availablePacksSearchTermSelector,
} from "features/Accounts/state/slices/availablePacksFiltersSlice";
import { type CardsViewerItem } from "components/cardsViewer/types";
import "./createAccountPacksList.scss";
import { type Filters } from "utils/queryUtils";
import { getFilterOptions } from "features/Licensing/Packs/state/thunks/packsFiltersThunk";
import { PackAssignmentModal } from "components";
import { bindAction } from "interfaces";
import { addPacksToAccount } from "features/Accounts/state/thunks/accountPacksThunk";
import { reset, setSelectedPacks } from "features/Accounts/state/slices/availablePacksSlice";
import { fetchAvailablePacks } from "features/Accounts/state/thunks/availablePacksThunk";
import type { GenericFiltersMap } from "utils/filterUtils";
import { getSelectedPackIds } from "features/Accounts/state/selectors/availablePacksSelector";
import { type AvailablePacksRequest } from "features/Accounts/types";
import { addDomainToAccount } from "features/Accounts/state/thunks/accountDomainsThunk";

export type CreateAccountPacksListProps = {
  createdAccountId: number;
  onConfirm: () => void;
  onCancel: () => void;
  isModalOpen: boolean;
} & PropsFromRedux;

const DEFAULT_ORDER_NAME = "DateCreated";
const IS_ADDED_ORDER = "isAdded desc";

export function CreateAccountPacksList({
  createdAccountId,
  resetPacksFilter,
  accountId,
  filter,
  availablePacks,
  getPacksFilterOptions,
  selectedPackIds,
  setSelectedPacks,
  applyPacksFilter,
  addPacksToAccount,
  addDomainToAccount,
  onConfirm,
  onCancel,
  setPacksSearch,
  resetAccountPacksGrid,
  fetchAccountPacksGrid,
  isModalOpen,
}: CreateAccountPacksListProps) {
  const [gridDataRequest, setGridDataRequest] = useState<{ abort: () => void }>();
  const [gridOrderBy, setGridOrderBy] = useState<SortOptions | "">("");

  const getPacksGridItems = (filters: Filters) => {
    if (createdAccountId === 0) {
      return;
    }

    gridDataRequest && gridDataRequest.abort();
    const loadedPacksCount = availablePacks.items.length;

    const request = fetchAccountPacksGrid({
      assignedAccountId: createdAccountId,
      skip: loadedPacksCount,
      orderBy: gridOrderBy === "" ? IS_ADDED_ORDER : gridOrderBy,
      filters,
      searchTerm: filter.searchTerm,
      allowAppend: true,
    });

    setGridDataRequest(request);
  };

  useEffect(() => {
    getPacksGridItems(filter.appliedFilter);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter.searchTerm, gridOrderBy]);

  useEffect(() => {
    return () => {
      resetAccountPacksGrid();
      resetPacksFilter();
      search("");
      setGridOrderBy(SortOptions.CreatedDateDesc);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleConfirmAddPacksModal = (packs: AssignAccountPack[], _: ViewType) => {
    onConfirm();

    addPacksToAccount(packs, createdAccountId);
    addDomainToAccount([createdAccountId]);
  };

  const search = (searchTerm: string) => {
    setPacksSearch(searchTerm);
    setSelectedPacks([]);
  };

  const isFiltered = !isEmpty(filter.searchTerm);

  const noResultsContent = () =>
    isFiltered ? (
      <NoSearchResults />
    ) : (
      <NoResults
        title="No packs for this account"
        description={
          <span>
            You don't have any packs that can be added to this account.
            <br />
          </span>
        }
        iconClassName="fa-box-full"
      />
    );

  const handleSortChange = (_: SyntheticEvent<HTMLElement>, data: DropdownProps) => {
    resetAccountPacksGrid();
    setGridOrderBy(data.value as SortOptions);
  };

  const resetPacks = (filter: Filters) => {
    setSelectedPacks([]);
    resetAccountPacksGrid();
    getPacksGridItems(filter);
  };

  const handleSearch = (query: string) => {
    resetAccountPacksGrid();
    search(query);
  };

  const renderCard = (props: CardsViewerItem<Item>) => <AddToAccountPackCard {...props} />;

  const resetFilter = () => {
    resetPacksFilter();
    resetPacks({});
  };

  const applyFilter = (filter: Filters) => {
    applyPacksFilter(filter);
    resetPacks(filter);
  };

  const handleSelectedItemsChanged = (ids: number[]) => {
    const newSelectedPacks: AssignAccountPack[] = ids.map((id) => {
      const pack = availablePacks.selected.find((sp) => sp.packId === id);
      if (pack) {
        return pack;
      }
      return { packId: id, isRequested: false, edited: false };
    });
    setSelectedPacks(newSelectedPacks);
  };

  const isItemSelected = (item: AccountPackOverview, base: boolean) => item.isAdded || base;

  // This component will be cloned and receives needed props inside FilterPanel component.
  // @ts-ignore
  const renderFilterForm = () => <PacksFilterForm />;

  return (
    <>
      <ItemsView
        className="account-packs-list"
        viewType={ViewType.GRID}
        renderCard={renderCard}
        getData={getPacksGridItems}
        itemsInlineCount={availablePacks.itemsCount}
        isLoading={availablePacks.isLoading}
        isAllDataLoaded={availablePacks.areAllLoaded}
        items={availablePacks.items}
        itemsType={ItemsTypes.Pack}
        sortingColumnName={DEFAULT_ORDER_NAME}
        sortingDirection={SortingDirection.Ascending}
        noResultsContent={noResultsContent()}
        withSelection
        renderSearch={() => (
          <SearchInput placeholder="Search for packs..." onChange={handleSearch} defaultValue={filter.searchTerm} />
        )}
        sortOptions={gridSortingOptions}
        orderBy={gridOrderBy}
        blur
        onSortChange={handleSortChange}
        doNotLoadPersistentViewType
        hideListGridViewSwitcherButton
        onSelectedItemsChanged={handleSelectedItemsChanged}
        selectedIds={selectedPackIds}
        isItemSelected={isItemSelected}
        renderFilterForm={renderFilterForm}
        filterOptions={filter.filterOptions}
        resetFilter={resetFilter}
        appliedFilter={filter.appliedFilter}
        filterOptionsLoading={filter?.isLoading}
        applyFilter={applyFilter}
        getFilterOptions={getPacksFilterOptions}
      />

      <PackAssignmentModal
        onConfirm={handleConfirmAddPacksModal}
        onCancel={onCancel}
        showModal={isModalOpen}
        selectAccountId={createdAccountId}
        accountId={accountId}
        loadPacksForModal={noop}
        loadPackForModal={noop}
        applyFilter={noop}
        resetFilter={noop}
        appliedFilter={noop}
        filterOptions={noop}
        fetchFilterOptions={noop}
        resetGrid={noop}
        isLoading={availablePacks.isLoading}
        areAllLoaded={availablePacks.areAllLoaded}
        items={availablePacks.items}
        itemsCount={availablePacks.itemsCount}
        selectedViewType={ViewType.GRID}
        isPacksModalFiltered
        predefinedPackIds={selectedPackIds}
      />
    </>
  );
}

/* istanbul ignore next */
const mapStateToProps = (state: RootState) => {
  const { userProfile } = state;

  return {
    accountId: userProfile.accountId,
    availablePacks: state.accounts.availablePacks,
    filter: {
      filterOptions: availablePacksFilterOptionsSelector(state),
      appliedFilter: availablePacksAppliedFiltersSelector(state),
      isLoading: availablePacksFilterIsLoadingSelector(state),
      searchTerm: availablePacksSearchTermSelector(state),
    },
    selectedPackIds: getSelectedPackIds(state),
  };
};

/* istanbul ignore next */
const mapDispatchToProps = (dispatch: AppDispatch) => {
  return {
    addPacksToAccount: bindAction(addPacksToAccount, dispatch),
    resetAccountPacksGrid: () => dispatch(reset()),
    fetchAccountPacksGrid: (requestData: AvailablePacksRequest) => dispatch(fetchAvailablePacks(requestData)),
    applyPacksFilter: (filters: GenericFiltersMap<string | number>) =>
      dispatch(availablePacksSetAppliedFilter(filters)),
    resetPacksFilter: () => dispatch(availablePacksResetAppliedFilter()),
    getPacksFilterOptions: () => dispatch(getFilterOptions(availablePacksFiltersSlice)),
    setPacksSearch: bindAction(availablePacksSetSearch, dispatch),
    setSelectedPacks: (packs: AssignAccountPack[]) => dispatch(setSelectedPacks(packs)),
    addDomainToAccount: bindAction(addDomainToAccount, dispatch),
  };
};

/* istanbul ignore next */
const connector = connect(mapStateToProps, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof connector>;
const ConnectedComponent = connector(CreateAccountPacksList);

export default ConnectedComponent;
